import React, { useState } from 'react';
import PropTypes from 'prop-types';
import LoadingIndicator from 'components/LoadingIndicator';
import { MD_BREAKPOINT_AND_UP } from 'utils/constants';
import { formatNumber } from 'utils/formatting';
import { userShape } from 'utils/shapes';
import withMediaQuery from 'utils/withMediaQuery';
import SapphireInfoModal from '../ProfileVault/SapphireInfoModal';
import CurrencyCardNew from './CurrencyCardNew';
import PromoSection from './PromoSection';
import SapphireTracker from './SapphireTracker';
import {
  CmpCurrencyPanelNew,
  PlsCurrencyPanelNew
} from './casinoCurrencyPanelNew';
import CreditsIcon from './assets/CreditsIcon.png';
import OnlineEntriesIcon from './assets/OnlineEntryIcon.png';
import SapphireIcon from './assets/SapphireIcon.png';
import WScoreIcon from './assets/WScoreIcon.png';
import './ProfileVaultNew.css';

const ProfileVaultNew = ({
  userData,
  assetsData,
  patronRewards,
  plsRewards,
  statusCmpData,
  statusPlsData,
  userEarningPotentialData,
  matchesBreakpoint: isTabletOrLarger
}) => {
  const [showInfoModal, setShowInfoModal] = useState(false);

  const statusData = statusCmpData || statusPlsData;
  const hasLoadingState = data => data && data.loading;
  const hasErrorState = data => data && data.error;

  const isLoading = [
    userEarningPotentialData,
    userData,
    statusData,
    assetsData
  ].some(hasLoadingState);
  const isError = [
    userEarningPotentialData,
    userData,
    statusData,
    assetsData
  ].some(hasErrorState);

  if (isLoading) {
    return <LoadingIndicator />;
  } else if (isError) {
    return (
      <div className="alert alert-danger">
        Failed to retrieve player currencies. If this continues, please contact
        support.
      </div>
    );
  }

  const { getUserEarningPotential } = userEarningPotentialData;
  const remainingGemEarningPotential =
    getUserEarningPotential?.remainingGemEarningPotential;
  const gemEarningPotential = getUserEarningPotential?.gemEarningPotential;
  const remainingSapphiresToEarn = Math.round(
    remainingGemEarningPotential * 10
  );
  const totalSapphiresToEarn = Math.round(gemEarningPotential * 10);

  const patronAssets =
    assetsData && assetsData.patronAssets
      ? assetsData.patronAssets
      : assetsData && assetsData.lookupPatronAssets;

  const casinoLocation = userData.user.preferredLocation;
  const CasinoCurrencyPanelComponent = statusCmpData
    ? CmpCurrencyPanelNew
    : PlsCurrencyPanelNew;

  const onlineCurrencies = [
    {
      amount: patronAssets.sapphires,
      label: 'Sapphires',
      format: formatNumber,
      icon: SapphireIcon,
      isSapphire: true
    },
    {
      amount: patronAssets.wscore,
      label: 'WScore',
      format: formatNumber,
      icon: WScoreIcon
    },
    {
      amount: patronAssets.gameBalance,
      label: 'Credits',
      format: formatNumber,
      icon: CreditsIcon
    },
    {
      amount: patronAssets.tickets,
      label: 'Online Entries',
      format: formatNumber,
      icon: OnlineEntriesIcon
    }
  ];

  const onlineCurrencyCards = onlineCurrencies.map(curr => (
    <CurrencyCardNew
      key={curr.label}
      amount={curr.amount}
      label={curr.label}
      format={curr.format}
      icon={curr.icon}
      isSapphire={curr.isSapphire ?? false}
      setShowInfoModal={setShowInfoModal}
    />
  ));

  const renderTabletOrLargerContent = () => (
    <div className="profile-vault-container">
      <SapphireInfoModal
        show={showInfoModal}
        onClose={() => setShowInfoModal(false)}
      />
      <div className="profile-vault-new">
        <div className="vault-currency-container">
          <div className="online-currency-panel">
            <div className="wallet-title">Online Wallet</div>
            {patronAssets && (
              <div className="currency-cards">{onlineCurrencyCards}</div>
            )}
          </div>
          <div className="casino-currency-panel">
            <div className="casino-loc">{casinoLocation} Wallet</div>
            {statusData && (
              <CasinoCurrencyPanelComponent statusData={statusData} />
            )}
          </div>
        </div>
        <div className="vault-right-column">
          <SapphireTracker
            remainingSapphiresToEarn={remainingSapphiresToEarn}
            totalSapphiresToEarn={totalSapphiresToEarn}
          />
          <PromoSection
            remainingSapphiresToEarn={remainingSapphiresToEarn}
            totalSapphiresToEarn={totalSapphiresToEarn}
            totalSapphiresEarned={patronAssets.sapphires}
            casinoLocation={casinoLocation}
            patronRewards={patronRewards}
            plsRewards={plsRewards}
          />
        </div>
      </div>
    </div>
  );

  const renderMobileContent = () => (
    <>
      <div className="profile-vault-new">
        <SapphireInfoModal
          show={showInfoModal}
          onClose={() => setShowInfoModal(false)}
        />
        <div className="wallet-title">Online Wallet</div>
        <SapphireTracker
          remainingSapphiresToEarn={remainingSapphiresToEarn}
          totalSapphiresToEarn={totalSapphiresToEarn}
        />
        <div className="online-currency-panel">
          {patronAssets && (
            <div className="currency-cards">{onlineCurrencyCards}</div>
          )}
        </div>
        <PromoSection
          remainingSapphiresToEarn={remainingSapphiresToEarn}
          totalSapphiresToEarn={totalSapphiresToEarn}
          totalSapphiresEarned={patronAssets.sapphires}
          casinoLocation={casinoLocation}
          patronRewards={patronRewards}
          plsRewards={plsRewards}
        />
        <div className="casino-loc">{casinoLocation} Wallet</div>
        <div className="casino-currency-panel">
          {statusData && (
            <CasinoCurrencyPanelComponent statusData={statusData} />
          )}
        </div>
      </div>
    </>
  );

  return isTabletOrLarger
    ? renderTabletOrLargerContent()
    : renderMobileContent();
};

ProfileVaultNew.propTypes = {
  userData: PropTypes.shape({
    user: userShape
  }),
  assetsData: PropTypes.shape({
    patronAssets: PropTypes.shape({
      gameBalance: PropTypes.number,
      nuggets: PropTypes.number,
      rubies: PropTypes.number,
      sapphires: PropTypes.number,
      tickets: PropTypes.number,
      wchips: PropTypes.number,
      wscore: PropTypes.number
    })
  }),
  statusCmpData: PropTypes.shape({
    statusv2: PropTypes.shape({
      cmpPlayerStatus: PropTypes.shape({
        freePlay: PropTypes.number,
        basePoints: PropTypes.number,
        drawings: PropTypes.arrayOf(
          PropTypes.shape({
            amount: PropTypes.number
          })
        )
      })
    })
  }),
  statusPlsData: PropTypes.shape({
    statusv2: PropTypes.shape({
      plsPlayerStatus: PropTypes.shape({
        freePlay: PropTypes.string,
        pointsBalance: PropTypes.number
      })
    })
  }),
  userEarningPotentialData: PropTypes.shape({
    getUserEarningPotential: PropTypes.shape({
      gemEarningPotential: PropTypes.number,
      remainingGemEarningPotential: PropTypes.number
    })
  }),
  patronRewards: PropTypes.arrayOf(
    PropTypes.shape({
      category: PropTypes.string,
      description: PropTypes.string
    })
  ),
  plsRewards: PropTypes.arrayOf(
    PropTypes.shape({
      category: PropTypes.string,
      remainingValue: PropTypes.string
    })
  )
};

export default withMediaQuery(MD_BREAKPOINT_AND_UP)(ProfileVaultNew);
